// src/utils/tokenManager.ts
import { msalInstance } from './msal';
import { loginRequest } from '@/config/authConfig';
import { ensureMsalInitialized } from './msal';

// Storage keys
const ACCESS_TOKEN_KEY = 'token';
const TOKEN_EXPIRE_AT_KEY = 'token_expire_at';
const REFRESH_THRESHOLD = 300; // 5 minutes before expiry

// Token refresh state management
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let refreshSubscribers: Array<(token: string) => void> = [];

/**
 * Save access token and expiry time to localStorage.
 */
export const setToken = (token: string, expiresIn: number) => {
  const expireAt = Date.now() + expiresIn * 1000;
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
  localStorage.setItem(TOKEN_EXPIRE_AT_KEY, expireAt.toString());
  console.log(`Token saved, expires in ${expiresIn} seconds`);
};

/**
 * Retrieve stored access token if not expired.
 */
export const getStoredToken = (): string | null => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const expireAt = localStorage.getItem(TOKEN_EXPIRE_AT_KEY);
  
  if (!token || !expireAt) {
    return null;
  }
  
  const expireTime = parseInt(expireAt, 10);
  if (Date.now() >= expireTime) {
    console.log('Stored token has expired');
    return null;
  }
  
  return token;
};

/**
 * Check if token needs refresh (within refresh threshold).
 */
export const shouldRefreshToken = (): boolean => {
  const expireAt = localStorage.getItem(TOKEN_EXPIRE_AT_KEY);
  if (!expireAt) return true;
  
  const expireTime = parseInt(expireAt, 10);
  const timeUntilExpiry = expireTime - Date.now();
  const refreshThresholdMs = REFRESH_THRESHOLD * 1000;
  
  return timeUntilExpiry <= refreshThresholdMs;
};

/**
 * Get valid access token, refreshing if necessary.
 * This is the main function to use for getting tokens.
 */
export const getValidToken = async (): Promise<string> => {
  // First check if we have a valid stored token
  const storedToken = getStoredToken();
  if (storedToken && !shouldRefreshToken()) {
    return storedToken;
  }
  
  // If we're already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  
  // Start refresh process
  if (!isRefreshing) {
    isRefreshing = true;
    refreshPromise = refreshToken();
  }
  
  try {
    const newToken = await refreshPromise!; // We know refreshPromise is not null here
    isRefreshing = false;
    refreshPromise = null;
    onRefreshed(newToken);
    return newToken;
  } catch (error) {
    isRefreshing = false;
    refreshPromise = null;
    throw error;
  }
};

/**
 * Refresh token using MSAL silent acquisition.
 */
async function refreshToken(): Promise<string> {
  await ensureMsalInitialized();
  
  const accounts = msalInstance.getAllAccounts();
  if (!accounts || accounts.length === 0) {
    throw new Error('No Azure account found for token refresh');
  }
  
  try {
    console.log('Attempting silent token refresh...');
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    
    if (!response.accessToken || !response.expiresOn) {
      throw new Error('Invalid token response from Azure AD');
    }
    
    const expiresInSeconds = Math.floor((response.expiresOn.getTime() - Date.now()) / 1000);
    setToken(response.accessToken, expiresInSeconds);
    
    console.log(`Token refresh successful, expires in ${expiresInSeconds} seconds`);
    return response.accessToken;
  } catch (error: any) {
    console.error('Silent token refresh failed:', error);
    
    // Handle specific MSAL errors
    if (error.errorCode === 'interaction_required') {
      throw new Error('User interaction required. Please login again.');
    } else if (error.errorCode === 'consent_required') {
      throw new Error('Additional consent required. Please login again.');
    } else if (error.errorCode === 'login_required') {
      throw new Error('Login required. Please authenticate again.');
    }
    
    throw new Error('Token refresh failed. Please login again.');
  }
}

/**
 * Notify subscribers that token has been refreshed.
 */
function onRefreshed(newToken: string) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

/**
 * Subscribe to token refresh events.
 * Useful for retrying failed requests after token refresh.
 */
export const subscribeToTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

/**
 * Clear stored tokens.
 */
export const clearToken = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRE_AT_KEY);
  console.log('Tokens cleared');
};

/**
 * Get token info for debugging.
 */
export const getTokenInfo = () => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const expireAt = localStorage.getItem(TOKEN_EXPIRE_AT_KEY);
  
  if (!token || !expireAt) {
    return { hasToken: false };
  }
  
  const expireTime = parseInt(expireAt, 10);
  const timeUntilExpiry = expireTime - Date.now();
  const needsRefresh = shouldRefreshToken();
  
  return {
    hasToken: true,
    expiresAt: new Date(expireTime).toISOString(),
    timeUntilExpiry: Math.floor(timeUntilExpiry / 1000),
    needsRefresh,
  };
};
